import { useNotification } from 'admin';
import { TrackedEvents, trackEvent } from 'analytics';
import {
  isApiFormError,
  isFieldErrorResponse,
  type MatchedSiteMaterialV2,
  useApiAccountsV1PortsRetrieve,
  useApiTradeV1MaterialListingOffersSendCreate,
  useApiTradeV1MaterialListingsOffersCreate,
} from 'api';
import { getFirstName } from 'features';
import { useFormContext, useWatch } from 'form';
import { logger } from 'monitor';
import { Button, formatNumber, formatPricePerTonne, Menu, MenuItem, Tooltip } from 'ui';
import { ExpandMore } from 'ui/icons';

import type { Recipient } from '../../types';
import type { DrawerFormValues } from '../MatchedSiteMaterialItemDrawer/MatchedSiteMaterialItemDrawer';

const useOfferMessage = ({ portId }: { portId?: number }) => {
  const { open } = useNotification();
  const { data: port } = useApiAccountsV1PortsRetrieve(portId ?? 0, {
    query: {
      enabled: Boolean(portId),
    },
  });

  return ({
    materialName,
    price,
    userName,
    url,
    incoterm,
    tonnes,
  }: {
    materialName: string;
    price: string;
    userName: string;
    url: string;
    incoterm: string;
    tonnes?: string;
  }) => {
    if (!port) {
      open?.({
        description: 'Please select a port of discharge',
        message: 'Error getting port details',
        type: 'error',
      });
      return;
    }

    return `Hi ${userName},

This ${materialName} matches your interests and ${tonnes ? `${tonnes} is available now` : 'may be available'}.

The guide price is ${price} ${incoterm} to ${port.name} ${port.unlocode}. You can see our AI analysis, images and data at the link. Let me know if you are interested.

${url}
`;
  };
};

export const BuyersTermsFormSender = ({
  disabled = false,
  siteMaterial,
  selectedEmployee,
  demandSignalId,
  supplySignalId,
  purchaserTradeCompanyId,
  tonnesAvailable,
}: {
  disabled?: boolean;
  siteMaterial: MatchedSiteMaterialV2;
  selectedEmployee?: Recipient;
  demandSignalId: number;
  supplySignalId: number;
  purchaserTradeCompanyId: number;
  tonnesAvailable?: string;
}) => {
  const form = useFormContext<DrawerFormValues>();
  const { setError, getValues } = form;
  const port = useWatch({
    control: form.control,
    name: 'port_of_discharge',
  });

  const { mutate: sendOffer, isPending: isOfferSending } = useApiTradeV1MaterialListingOffersSendCreate({
    mutation: {
      onError(error) {
        if (isFieldErrorResponse(error)) {
          open?.({
            description: error?.response?.data.non_field_errors[0],
            message: 'Error sending offer.',
            type: 'error',
          });
          return;
        }
        open?.({
          message: 'Error sending offer.',
          type: 'error',
        });
      },
      onSuccess() {
        open?.({ message: 'Offer automatically sent.', type: 'success' });
        trackEvent(TrackedEvents.TRADING_DASHBOARD_SEND_AUTO, {
          terms: form.getValues(),
          type: 'buyer',
        });
      },
    },
  });

  const createOfferMessage = useOfferMessage({
    portId: port,
  });
  const { open } = useNotification();

  const { mutateAsync: createBuyerOffer, isPending: isBuyerOfferCreating } = useApiTradeV1MaterialListingsOffersCreate({
    mutation: {
      onError(error) {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            setError(field as keyof DrawerFormValues, {
              message: error.response.data[field],
            });
          });
          if (error.response?.data.non_field_errors) {
            open?.({
              description: error?.response?.data.non_field_errors.toString(),
              message: 'Error creating offer',
              type: 'error',
            });
          }
          return;
        }
        if (isApiFormError(error)) {
          open?.({
            description: error?.response?.data.detail,
            message: 'Error creating offer',
            type: 'error',
          });
          return;
        }
        open?.({
          message: 'Error creating offer.',
          type: 'error',
        });
      },
    },
  });

  const createOffer = () => {
    const values = getValues();

    if (!selectedEmployee) {
      open?.({
        description: 'Please select a recipient to send an offer',
        message: 'Error creating offer',
        type: 'error',
      });
      return;
    }

    return createBuyerOffer({
      data: {
        demand_signal_id: demandSignalId,
        incoterm: values.demand_incoterm,
        port_of_discharge_id: values.port_of_discharge,
        price_per_tonne: values.buyer_price_per_tonne.toString(),
        price_per_tonne_currency: values.buyer_currency,
        purchaser_trade_company_id: purchaserTradeCompanyId,
        purchaser_user_id: selectedEmployee?.id,
        site_material_id: siteMaterial.id,
        supply_signal_id: supplySignalId,
        tonnes: String(values.number_of_loads * values.tonnes_per_load),
      },
    });
  };

  const handleAutoOffer = async () => {
    const offer = await createOffer();
    if (!offer) {
      logger.error('Failed to create offer', { offer });
      return;
    }
    sendOffer({
      offerId: offer.id,
    });
  };

  const handleEmailOffer = () => {
    handleManualOffer('email');
  };

  const handleWhatsappOffer = () => {
    handleManualOffer('whatsapp');
  };

  const handleManualOffer = async (channel: 'whatsapp' | 'email') => {
    const offer = await createOffer();
    if (!offer) {
      logger.error('Failed to create offer', { offer });
      return;
    }
    const values = getValues();

    const {
      purchaser_user: { phone_number, full_name, email },
    } = offer;

    const firstName = getFirstName(full_name);
    const formattedPrice = formatPricePerTonne(offer.price_per_tonne, values.buyer_currency);
    const materialName = `${siteMaterial.material_spec.material.title} ${siteMaterial.grade.name}`;

    const message = createOfferMessage({
      incoterm: values.demand_incoterm,
      materialName,
      price: formattedPrice,
      tonnes: tonnesAvailable
        ? formatNumber(Number(tonnesAvailable), {
            style: 'ton',
          })
        : undefined,
      url: `${window.location.origin}/buy/${offer.material_listing_public_id}/`,
      userName: firstName,
    });

    if (!message) {
      logger.error('Failed to create offer message', { offer, values });
      return;
    }

    if (channel === 'email') {
      const subject = `New supply of ${materialName}`;

      window.open(
        `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`,
        '_blank'
      );

      trackEvent(TrackedEvents.TRADING_DASHBOARD_SEND_MANUAL, {
        channel: 'email',
        message,
        offer,
        terms: values,
        type: 'buyer',
      });
      return;
    }
    if (channel === 'whatsapp') {
      window.open(`https://wa.me/${phone_number}?text=${encodeURIComponent(message)}`, '_blank');

      trackEvent(TrackedEvents.TRADING_DASHBOARD_SEND_MANUAL, {
        channel: 'whatsapp',
        message,
        offer,
        terms: values,
        type: 'buyer',
      });
      return;
    }
  };

  return (
    <Menu
      button={
        <Tooltip title={!selectedEmployee ? 'Select a recipient to send an offer' : null}>
          <Button
            variant="secondary"
            size="medium"
            type="submit" // Needed to trigger form validation
            endIcon={<ExpandMore />}
            disabled={disabled}
            loading={isBuyerOfferCreating || isOfferSending}
          >
            Send offer to buyer
          </Button>
        </Tooltip>
      }
    >
      <MenuItem onClick={handleAutoOffer} disabled={!selectedEmployee}>
        Auto offer
      </MenuItem>
      <MenuItem onClick={handleWhatsappOffer} disabled={!selectedEmployee?.phone_number}>
        Whatsapp
      </MenuItem>
      <MenuItem onClick={handleEmailOffer} disabled={!selectedEmployee}>
        Email
      </MenuItem>
    </Menu>
  );
};
